import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImFolder, ImFolderOpen, ImBook, ImParagraphLeft, ImFileText } from "react-icons/im";

import { UseOutletContextPlan } from './Plan';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AccountingAccountDA } from '../../../../services/admin/accounting.account.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Names } from '../../../../models/names.model';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { CompanyAccountingTreeAccounts } from '../../../../models/company.accounting.tree.accounts.model';
import { getError } from '../../../../languages/translations/response';
import { accountingAccountTypeValue } from '../../../../libraries/account.type.library';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { modalShow } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';
import AppModalAccountCreate from '../../../../components/modal/ModalAccountCreate';
import AppModalAccountRename from '../../../../components/modal/ModalAccountRename';

export interface PlanListPageProps {};

let errorResponse: Error, accountingAccountsResponse: CompanyAccountingTreeAccounts;

const PlanListPage: React.FunctionComponent<PlanListPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextPlan()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchRef = useRef('')
  const [filter, setFilter] = useState({search: '', options: {sort_field: '', sort_mode: ''}})
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[] | null>(null)
  const [createModalMode, setCreateModalMode] = useState<'sibling' | 'child'>('sibling')
  const [createModalSource, setCreateModalSource] = useState<AccountingAccount | null>(null)
  const [createModalParent, setCreateModalParent] = useState<AccountingAccount | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [renameAccount, setRenameAccount] = useState<AccountingAccount | null>(null)

  const loadAccountingAccounts = async (id_company: string) => {
    if (companyForUser) {
      await AccountingAccountDA.getAccountingTreeAccounts(id_company, companyForUser.language, searchRef.current, filter.options.sort_field, filter.options.sort_mode).then( (response) => {
        if (response.status === 200) {
          accountingAccountsResponse = response.data

          setAccountingAccounts(accountingAccountsResponse.accounting_accounts)
          setFilter({search: searchRef.current, options: {sort_field: accountingAccountsResponse.filter.sort_field, sort_mode: accountingAccountsResponse.filter.sort_mode}})
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      })
    }
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (companyForUser) {
      const text = event.target.value
      setSearchText(text)
      searchRef.current = text
      filter.options.sort_field = ''
      filter.options.sort_mode = ''
      loadAccountingAccounts(companyForUser.id)
    }
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      loadAccountingAccounts(companyForUser.id)
    }
  }

  const findParentInTree = (tree: AccountingAccount[], targetId: string): AccountingAccount | null => {
    for (const node of tree) {
      if ((node.children ?? []).some(c => c.id === targetId)) return node
      const found = findParentInTree(node.children ?? [], targetId)
      if (found) return found
    }
    return null
  }

  const handleCreateSibling = (account: AccountingAccount) => {
    const parent = accountingAccounts ? findParentInTree(accountingAccounts, account.id) : null
    setCreateModalMode('sibling')
    setCreateModalSource(account)
    setCreateModalParent(parent)
    modalShow('modal-account-create')
  }

  const handleCreateChild = (account: AccountingAccount) => {
    setCreateModalMode('child')
    setCreateModalSource(account)
    setCreateModalParent(null)
    modalShow('modal-account-create')
  }

  useEffect( () => {
    if (companyForUser) {
      setAccountingAccounts(null)
      loadAccountingAccounts(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.listAccountingAccounts}, company: true})

    const handleClickOutside = () => setOpenMenuId(null)
    document.addEventListener('click', handleClickOutside)

    return () => {
      setMounted(false)
      document.removeEventListener('click', handleClickOutside)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && accountingAccounts
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchAccountingAccount} value={searchText} onChange={handleChangeSearch} />
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-50px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("code")}>
                          <span className="me-1">{lang.labels.code}</span>
                          <AppSortTable attribute={"code"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-300px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("name")}>
                          <span className="me-1">{lang.labels.name}</span>
                          <AppSortTable attribute={"name"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-75px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("type")}>
                          <span className="me-1">{lang.labels.type}</span>
                          <AppSortTable attribute={"type"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-75px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("level")}>
                          <span className="me-1">{lang.labels.level}</span>
                          <AppSortTable attribute={"level"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-75px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("coefficient")}>
                          <span className="me-1">{lang.labels.coefficient}</span>
                          <AppSortTable attribute={"coefficient"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-75px">{lang.labels.currencyColumn}</th>
                      <th className="w-50px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { accountingAccounts.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { accountingAccounts.map (( (item, index) => { return (
                        <React.Fragment key={index}>
                          <tr className="row-dashed">
                            <td className="text-dark">{item.code}</td>
                            <td>
                              <div className="d-flex align-items-center ms-0">
                                <span className="d-flex align-items-start me-2"><ImFolder /></span>
                                <label className="text-dark fw-bold">{item.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</label>
                              </div>
                            </td>
                            <td>{accountingAccountTypeValue(lang, item.type)}</td>
                            <td>{item.level}</td>
                            <td>{item.coefficient}</td>
                            <td></td>
                            <td></td>
                          </tr>
                          { (item.children ?? []).map (( (sub_item, sub_index) => { return (
                            <React.Fragment key={sub_index}>
                              <tr className="row-dashed">
                                <td className="text-dark">{sub_item.code}</td>
                                <td>
                                  <div className="d-flex align-items-center ms-5">
                                    <span className="d-flex align-items-start me-2"><ImFolderOpen /></span>
                                    <label className="text-dark fw-bold">{sub_item.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</label>
                                  </div>
                                </td>
                                <td>{accountingAccountTypeValue(lang, sub_item.type)}</td>
                                <td>{sub_item.level}</td>
                                <td>{sub_item.coefficient}</td>
                                <td></td>
                                <td></td>
                              </tr>
                              { (sub_item.children ?? []).map (( (sub_sub_item, sub_sub_index) => { return (
                                <React.Fragment key={sub_sub_index}>
                                  <tr className="row-dashed">
                                    <td className="text-dark">{sub_sub_item.code}</td>
                                    <td>
                                      <div className="d-flex align-items-center ms-10">
                                        <span className="d-flex align-items-start me-2"><ImBook /></span>
                                        <label className="text-dark fw-bold">{sub_sub_item.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</label>
                                      </div>
                                    </td>
                                    <td>{accountingAccountTypeValue(lang, sub_sub_item.type)}</td>
                                    <td>{sub_sub_item.level}</td>
                                    <td>{sub_sub_item.coefficient}</td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                  { (sub_sub_item.children ?? []).map (( (sub_sub_sub_item, sub_sub_sub_index) => { return (
                                    <React.Fragment key={sub_sub_sub_index}>
                                      <tr className="row-dashed">
                                        <td className="text-dark">{sub_sub_sub_item.code}</td>
                                        <td>
                                          <div className={`d-flex align-items-center ${sub_sub_sub_item.type === "title" ? "ps-15" : "ps-20"}`}>
                                            { sub_sub_sub_item.type === "title"
                                              ?
                                              <>
                                                <span className="d-flex align-items-start me-2"><ImParagraphLeft /></span>
                                                <label className="text-dark fw-bold">{sub_sub_sub_item.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</label>
                                              </>
                                              :
                                              <>
                                                <span className="d-flex align-items-start me-2"><ImFileText /></span>
                                                <Link to={"/main/accounting/plan/view/" + sub_sub_sub_item.id} className="text-dark fw-bolder text-hover-primary">{sub_sub_sub_item.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Link>
                                              </>
                                            }
                                          </div>
                                        </td>
                                        <td>{accountingAccountTypeValue(lang, sub_sub_sub_item.type)}</td>
                                        <td>{sub_sub_sub_item.level}</td>
                                        <td>{sub_sub_sub_item.coefficient}</td>
                                        <td>
                                          { sub_sub_sub_item.currency &&
                                            <span className={`badge ${sub_sub_sub_item.currency.code !== 'CHF' ? 'badge-light-primary' : 'badge-light'}`}>
                                              {sub_sub_sub_item.currency.code}
                                            </span>
                                          }
                                        </td>
                                        <td className="text-end">
                                          { sub_sub_sub_item.type === "leave" &&
                                            <div className="position-relative d-inline-block">
                                              <button className="btn btn-sm btn-icon btn-light btn-active-light-primary" type="button" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === sub_sub_sub_item.id ? null : sub_sub_sub_item.id) }}>
                                                <span className="svg-icon svg-icon-5 m-0">&#8942;</span>
                                              </button>
                                              { openMenuId === sub_sub_sub_item.id &&
                                                <div className="dropdown-menu dropdown-menu-end show position-absolute" style={{top: '100%', right: 0, zIndex: 1050}} onClick={(e) => e.stopPropagation()}>
                                                  <button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); setRenameAccount(sub_sub_sub_item); modalShow('modal-account-rename') }}>{lang.labels.renameAccount}</button>
                                                  <button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handleCreateSibling(sub_sub_sub_item) }}>{lang.labels.createSiblingAccount}</button>
                                                  <button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handleCreateChild(sub_sub_sub_item) }}>{lang.labels.createChildAccount}</button>
                                                </div>
                                              }
                                            </div>
                                          }
                                        </td>
                                      </tr>
                                    </React.Fragment>
                                  )}))}
                                </React.Fragment>
                              )}))}
                            </React.Fragment>
                          )}))}
                        </React.Fragment>
                      )}))}
                    </tbody>
                    :
                    <tbody> 
                      <tr className="text-secondary fw-bold">
                        <td colSpan={7} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalAccountCreate companyForUser={companyForUser} mode={createModalMode} sourceAccount={createModalSource} parentAccount={createModalParent} loadFunction={loadAccountingAccounts}></AppModalAccountCreate>
      <AppModalAccountRename companyForUser={companyForUser} account={renameAccount} loadFunction={loadAccountingAccounts}></AppModalAccountRename>
    </div>
  )
};

export default PlanListPage;
